import { useEffect, useRef } from "react";

/** Wie lange das Runden-Recap offen bleibt, bevor es sich selbst schliesst. */
export const RECAP_AUTO_CLOSE_MS = 15000;

/**
 * Schliesst ein Overlay nach `delay` ms von selbst.
 *
 * Warum als eigener Hook und nicht als useEffect direkt in App.js:
 * so laesst sich genau das Verhalten testen, auf das es hier ankommt
 * (kein doppelter Aufruf nach manuellem Schliessen, Neustart pro Runde,
 * sauberes Aufraeumen beim Unmount) - ohne die ganze App mit Socket-Mocks
 * hochziehen zu muessen.
 *
 * @param active   true, solange das Overlay sichtbar ist
 * @param onClose  derselbe Handler wie der "Schliessen"-Button
 * @param delay    Wartezeit in ms
 * @param resetKey Wechselt dieser Wert, startet die Zeit neu. Fuer das
 *                 Runden-Recap wird das recap-Objekt selbst uebergeben:
 *                 jede Runde erzeugt ein neues Objekt, also faengt der
 *                 Timer bei Runde N+1 wieder bei 0 an - auch dann, wenn
 *                 zwei Runden schnell hintereinander enden.
 */
export function useAutoClose(active, onClose, { delay = RECAP_AUTO_CLOSE_MS, resetKey } = {}) {
  // Der Handler liegt bewusst in einer Ref: kaeme er direkt in die
  // Abhaengigkeiten, wuerde ein inline definierter Callback (neue Identitaet
  // bei jedem Render) den Timer bei jedem Render neu starten - er liefe dann
  // nie ab. So bleibt der Timer stabil und ruft trotzdem immer die aktuelle
  // Funktion auf.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!active) return undefined;
    const id = setTimeout(() => onCloseRef.current?.(), delay);
    // Cleanup deckt beide Faelle ab: manuelles Schliessen (active wird false)
    // und Unmount. Danach kann der Handler nicht mehr ein zweites Mal feuern.
    return () => clearTimeout(id);
  }, [active, delay, resetKey]);
}

/**
 * Darf das Runden-Recap sich gerade selbst schliessen?
 * Nur in normalen Runden. Sobald das Spiel vorbei ist (gameOver gesetzt),
 * gilt KEIN Zeitlimit mehr: das Recap der letzten Runde und danach das
 * Spielende-Modal bleiben offen, bis der Spieler sie selbst schliesst.
 * Wechselt der Wert waehrend eines laufenden Timers auf false (gameOver kommt
 * ~1,8 s nach roundEnd), verwirft useAutoClose den Timer im Cleanup.
 */
export function recapAutoCloseActive({ showRecap, recap, gameOver }) {
  return !!showRecap && !!recap && !gameOver;
}
